import moment from "moment";
import { CreditReportConsumerInformation } from "types/landlord/reports";
import { Row } from "views/applications-id-report/components/Row";
import { Section } from "views/applications-id-report/components/Section";
import { EMPTY_VALUE } from "views/applications-id-report/utils/constants";

interface ConsumerInformationProps {
  consumer_information?: CreditReportConsumerInformation;
  isNoHit: boolean;
}

const leftColumnFields = [
  { title: "Other names", valueKey: "other_names" },
  { title: "Current address", valueKey: ["address1", "city", "state", "zip"] },
  {
    title: "Date reported address",
    valueKey: "date_reported_address",
    type: "date"
  },
  {
    title: "Address variance indicator",
    valueKey: "address_variance_indicator"
  },
  { title: "Current phone number", valueKey: "current_phone_number" },
  { title: "Date reported phone", valueKey: "date_reported_phone_number" }
];

const rightColumnFields = [
  { title: "SSN status", valueKey: "ssn_status" },
  { title: "SSN match flags", valueKey: "ssn_match_flags" },
  { title: "Issue date", valueKey: "issue_date", type: "date" },
  { title: "Issue state", valueKey: "issue_state" },
  { title: "Death date", valueKey: "death_date", type: "date" },
  {
    title: "Date file was established",
    valueKey: "date_file_established",
    type: "date"
  },
  {
    title: "Date of most recent activity",
    valueKey: "date_most_recent_activity",
    type: "date"
  }
];

const toDisplayValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  if (Array.isArray(value))
    return value.filter(Boolean).join(", ") || undefined;
  if (typeof value === "object") {
    const coded = value as {
      code?: string | null;
      description?: string | null;
    };
    return coded.description || coded.code || undefined;
  }

  return String(value);
};

export const ConsumerInformation = ({
  consumer_information,
  isNoHit
}: ConsumerInformationProps) => {
  if (isNoHit)
    return (
      <Section sectionTitle="Consumer information" maxWidth="none">
        <p className="text-base font-medium text-content-300">
          No data available
        </p>
      </Section>
    );

  return (
    <Section sectionTitle="Consumer information" maxWidth="none">
      <div className="flex w-full flex-row flex-wrap gap-4">
        {/* Left column */}
        <div className="w-[360px]">
          <div>
            {leftColumnFields.map((l) => {
              if (Array.isArray(l.valueKey)) {
                // Joining absent parts would render a row of bare separators.
                const value = l.valueKey
                  .map((key) => consumer_information?.[key])
                  .filter(Boolean)
                  .join(", ");
                return (
                  <Row
                    className="my-3"
                    key={l.title}
                    title={l.title}
                    value={value || EMPTY_VALUE}
                  />
                );
              }

              const value =
                l.type === "date" &&
                consumer_information?.[l.valueKey] &&
                moment(consumer_information[l.valueKey]).isValid()
                  ? moment(consumer_information[l.valueKey]).format(
                      "MMM DD, YYYY"
                    )
                  : consumer_information?.[l.valueKey];

              return (
                <Row
                  className="my-3"
                  key={l.title}
                  title={l.title}
                  value={toDisplayValue(value) || EMPTY_VALUE}
                />
              );
            })}
          </div>
        </div>

        {/* Right column */}
        <div className="w-[360px]">
          {rightColumnFields.map((r) => {
            const value =
              r.type === "date" &&
              consumer_information?.[r.valueKey] &&
              moment(consumer_information[r.valueKey]).isValid()
                ? moment(consumer_information[r.valueKey]).format(
                    "MMM DD, YYYY"
                  )
                : consumer_information?.[r.valueKey];

            return (
              <Row
                className="my-3"
                key={r.title}
                title={r.title}
                value={toDisplayValue(value) || EMPTY_VALUE}
              />
            );
          })}
        </div>
      </div>
    </Section>
  );
};
